import React, { useEffect, useMemo } from 'react'
import { Typography, Box, TextField, Tooltip } from '@mui/material'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircle, faCheck } from '@fortawesome/free-solid-svg-icons'
import { SearchableSelect } from 'components'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'
import { QuestionMarkIcon, MailIcon, SplitIcon, InquiryIcon } from './icons'
import { TFilterOption } from 'components/CaseBuilderIssuer/CaseBuilderIssuer.vm'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { DatePickerSecondaryWrapper } from 'components/CaseBuilderIssuerFormBuilder/formTypes/RenderDateField'

export const updatedToRGBA = (color: string, alpha = 1) => {
    // Hex format: #RRGGBB or #RGB
    if (/^#([0-9A-Fa-f]{3}){1,2}$/.test(color)) {
        let r, g, b

        // Expand shorthand hex like #abc to #aabbcc
        if (color.length === 4) {
            r = parseInt(color[1] + color[1], 16)
            g = parseInt(color[2] + color[2], 16)
            b = parseInt(color[3] + color[3], 16)
        } else {
            r = parseInt(color.slice(1, 3), 16)
            g = parseInt(color.slice(3, 5), 16)
            b = parseInt(color.slice(5, 7), 16)
        }

        return `rgba(${r}, ${g}, ${b}, ${alpha})`
    }

    // rgb() format
    if (/^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/.test(color)) {
        return color.replace('rgb', 'rgba').replace(')', `, ${alpha})`)
    }

    // rgba() format — override existing alpha
    const rgbaMatch = color.match(
        /^rgba\(\s*(\d+),\s*(\d+),\s*(\d+),\s*(0|1|0?\.\d+)\s*\)$/
    )
    if (rgbaMatch) {
        //@typescript-eslint/no-unused-vars
        const [, r, g, b] = rgbaMatch
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
    }

    // fallback
    console.warn(`Invalid color format: ${color}`)
    return `rgba(0, 0, 0, ${alpha})` // default fallback
}

interface IDisputeType {
    id: number
    title: string
    subTitle: string
    icon: any
}

interface IInitiateDisputeStep1Props {
    accountNumberList: TFilterOption[]
    disputeReasonList: TFilterOption[]
    setSelectedDisputeReasonId: (selectedId: number | null) => void
    formInfo: { [key: string]: any }
    handleChangeFormField: (accessor: string, input: any) => void
    isEnableAutofillChecked: boolean
    setIsEnableAutofillChecked: (isChecked: boolean) => void
    setEnableSaveAndContinue: (enable: boolean) => void
    className?: string
}

interface CaseBuilderIssuerFormLabelProps {
    labelText: string
    isRequired?: boolean
    className?: string
    infoTooltipText?: string
}

export const CaseBuilderIssuerFormLabel = ({
    labelText,
    isRequired = false,
    infoTooltipText,
    className = '',
}: CaseBuilderIssuerFormLabelProps) => {
    const InfoIcon = (
        <FontAwesomeIcon
            icon={faInfoCircle}
            className="emp-caseBuilderIssuerFormLabel-infoIcon"
        />
    )

    return (
        <Typography
            className={`emp-caseBuilderIssuerFormLabel-root ${className}`}
        >
            {labelText}&nbsp;
            {isRequired && <span style={{ color: 'red' }}>*</span>}
            {infoTooltipText && (
                <Tooltip arrow placement="top" title={infoTooltipText}>
                    <span>{InfoIcon}</span>
                </Tooltip>
            )}
        </Typography>
    )
}

const TRANSACTION_ID_TOOLTIP_MAP: Record<number, string> = {
    1: 'ARN', // DISPUTE_PRO
    2: 'ARN', // OUTPAYCE
    3: 'Deployment ID', // LASTMINUTE
    4: 'Processor Reference Number', // KRAKEN
    5: 'Processor Reference Number', // MONAVATE
    6: 'ARN', // AMADEUS
    7: 'Deployment ID', // SABRE
} as const

const DEFAULT_TOOLTIP_TEXT = 'ARN'

const getTransactionIDTooltipText = (themeVariantId?: number): string => {
    if (!themeVariantId) return DEFAULT_TOOLTIP_TEXT

    return TRANSACTION_ID_TOOLTIP_MAP[themeVariantId] ?? DEFAULT_TOOLTIP_TEXT
}

export const InitiateDisputeStep1 = ({
    accountNumberList,
    disputeReasonList,
    setSelectedDisputeReasonId,
    formInfo,
    handleChangeFormField,
    // isEnableAutofillChecked,
    // setIsEnableAutofillChecked,
    setEnableSaveAndContinue,
    className = '',
}: IInitiateDisputeStep1Props) => {
    const { clientTheme, themeVariantObj } = useThemeStyling()

    const stylesObj = {
        textInputField: {
            borderRadius: clientTheme.selectionBox.borderRadius,
            '& > div input': {
                padding: '7px',
            },
            '& > .MuiSelect-select': {
                padding: '7px',
            },
            '& .MuiOutlinedInput-root': {
                borderRadius: clientTheme.selectionBox.borderRadius,
            },
            '& .MuiInputBase-input': {
                color: clientTheme.typography.fontColor.primaryText,
            },
            '& .MuiSvgIcon-root': {
                fontSize: '18px',
            },
        },
    }

    useEffect(() => {
        const enableBtn =
            formInfo.accountNumber &&
            formInfo.disputeReason &&
            formInfo.disputeType &&
            formInfo.transactionId &&
            formInfo.transactionDate
        setEnableSaveAndContinue(enableBtn)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formInfo])

    const disputeTypes: IDisputeType[] = [
        {
            id: 1,
            title: 'Purchase Insight ',
            subTitle: 'Request information about the merchant and transaction',
            icon: QuestionMarkIcon,
        },
        {
            id: 2,
            title: 'Merchant Request',
            subTitle:
                'Request merchant to resolve issue by a refund or feedback',
            icon: InquiryIcon,
        },
        {
            id: 3,
            title: 'Raise a Chargeback',
            subTitle: 'Initiate a formal dispute and raise a chargeback case',
            icon: MailIcon,
        },
        {
            id: 4,
            title: 'Split Request',
            subTitle: 'Request a 50% refund from the merchant to resolve',
            icon: SplitIcon,
        },
    ]

    const transactionIDTooltipText = useMemo(
        () => getTransactionIDTooltipText(themeVariantObj.id),
        [themeVariantObj.id]
    )

    return (
        <div className={`emp-initiateDisputeStep1-root ${className}`}>
            {/* section 1 */}
            <div className="emp-initiateDisputeStep1-section">
                <div>
                    <Typography className="emp-initiateDisputeStep1-header">
                        Initiate Dispute
                    </Typography>
                    <Typography className="emp-initiateDisputeStep1-subHeader">
                        To initiate a transaction dispute please provider the
                        following information.
                    </Typography>
                </div>
                <div className="emp-initiateDisputeStep1-formFields">
                    <div>
                        <CaseBuilderIssuerFormLabel
                            labelText={'Account Number'}
                            isRequired
                        />
                        <div>
                            <SearchableSelect
                                placeholder={'Select account number'}
                                value={formInfo.accountNumber ?? ''}
                                onValueChange={(e) => {
                                    handleChangeFormField(
                                        'accountNumber',
                                        e?.name
                                    )
                                }}
                                options={accountNumberList ?? []}
                                accessor={'name'}
                                testId={'accountNumber'}
                                hideSearch
                                selectFontSize={
                                    clientTheme.typography.body1.fontSize
                                }
                                isPaginate={false}
                            />
                        </div>
                    </div>
                    <div>
                        <CaseBuilderIssuerFormLabel
                            labelText={'Dispute Reason'}
                            isRequired
                        />

                        <div>
                            <SearchableSelect
                                value={formInfo.disputeReason ?? ''}
                                placeholder={'Select dispute reason'}
                                onValueChange={(e) => {
                                    setSelectedDisputeReasonId(e?.id ?? null)
                                    handleChangeFormField(
                                        'disputeReason',
                                        e?.name
                                    )
                                }}
                                options={disputeReasonList ?? []}
                                accessor={'name'}
                                testId={'disputeReason'}
                                hideSearch
                                selectFontSize={
                                    clientTheme.typography.body1.fontSize
                                }
                                isPaginate={false}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {/* section 2 */}
            <div className="emp-initiateDisputeStep1-section">
                <div>
                    <Typography className="emp-initiateDisputeStep1-header">
                        Dispute Type
                    </Typography>
                    <Typography className="emp-initiateDisputeStep1-subHeader">
                        Select the type of dispute you would like to create.
                        <span style={{ color: 'red' }}>*</span>
                    </Typography>
                </div>
                <div className="emp-initiateDisputeStep1-disputeTypesContainer">
                    {disputeTypes.map((type: IDisputeType) => {
                        const IconComponent = type.icon
                        return (
                            <Box
                                key={`key-disputeType-${type.id}`}
                                onClick={() =>
                                    handleChangeFormField(
                                        'disputeType',
                                        type.title
                                    )
                                }
                                className={
                                    'emp-initiateDisputeStep1-disputeType'
                                }
                                sx={[
                                    {
                                        '&:hover': {
                                            border: `1px solid ${clientTheme.caseBuilder.color}`,
                                        },
                                    },
                                    formInfo.disputeType === type.title && {
                                        border: `1px solid ${clientTheme.caseBuilder.color} !important`,
                                        boxShadow: `0px 0px 8px 0px ${updatedToRGBA(
                                            clientTheme.caseBuilder.color,
                                            0.3
                                        )}`,
                                        backgroundColor: `${updatedToRGBA(
                                            clientTheme.caseBuilder.color,
                                            0.05
                                        )} !important`,
                                        'p:first-of-type ': {
                                            color:
                                                clientTheme.caseBuilder.color,
                                        },
                                    },
                                ]}
                            >
                                <IconComponent
                                    iconColor={
                                        formInfo.disputeType === type.title
                                            ? clientTheme.caseBuilder.color
                                            : '#333'
                                    }
                                />
                                <Typography style={{ fontWeight: 600 }}>
                                    {type.title}
                                </Typography>
                                <Typography className="emp-initiateDisputeStep1-subHeaderDisputeType">
                                    {type.subTitle}
                                </Typography>
                                {formInfo.disputeType === type.title && (
                                    // Unable to use FontAwesome's faCheckCircle and needed two Icons bc check was showing background and if you make background white is adds a box that cuts into the shadowing
                                    <span>
                                        <FontAwesomeIcon
                                            icon={faCircle}
                                            style={{
                                                color:
                                                    clientTheme.caseBuilder
                                                        .color,
                                            }}
                                            className="emp-initiateDisputeStep1-checkIcon"
                                        />
                                        <FontAwesomeIcon
                                            icon={faCheck}
                                            style={{ color: 'white' }}
                                            transform="shrink-6"
                                            className="emp-initiateDisputeStep1-checkIcon"
                                        />
                                    </span>
                                )}
                            </Box>
                        )
                    })}
                </div>
            </div>
            {/* section 3 */}
            <div className="emp-initiateDisputeStep1-section">
                <div className="emp-initiateDisputeStep1-formFields">
                    <div>
                        <CaseBuilderIssuerFormLabel
                            labelText={'Transaction ID'}
                            isRequired
                            infoTooltipText={transactionIDTooltipText}
                        />

                        <div>
                            <TextField
                                variant={'outlined'}
                                sx={stylesObj.textInputField}
                                required={false}
                                type="text"
                                name={'transactionId'}
                                fullWidth
                                placeholder={'Enter the transaction ID'}
                                value={formInfo.transactionId ?? ''}
                                onChange={(e) => {
                                    handleChangeFormField(
                                        'transactionId',
                                        e?.target.value
                                    )
                                }}
                            />
                        </div>
                    </div>
                    <div>
                        <CaseBuilderIssuerFormLabel
                            labelText={'Transaction Date'}
                            isRequired
                        />

                        <div>
                            <DatePickerSecondaryWrapper>
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                >
                                    <DatePicker
                                        inputFormat="YYYY/MM/DD"
                                        value={
                                            formInfo.transactionDate
                                                ? dayjs(
                                                      formInfo.transactionDate
                                                  )
                                                : null
                                        }
                                        onChange={(newValue) => {
                                            handleChangeFormField(
                                                'transactionDate',
                                                newValue
                                                    ? newValue.format(
                                                          'YYYY-MM-DD'
                                                      )
                                                    : ''
                                            )
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                sx={stylesObj.textInputField}
                                                required={false}
                                                fullWidth
                                                name="transactionDate"
                                                placeholder="YYYY/MM/DD"
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            </DatePickerSecondaryWrapper>
                        </div>
                    </div>
                </div>
            </div>
            {/* section 4 */}
            {/* <div className="emp-initiateDisputeStep1-section">
                <div
                    className={
                        'emp-initiateDisputeStep1-enableAutofillContainer'
                    }
                >
                    <Checkbox
                        checked={isEnableAutofillChecked}
                        onChange={() =>
                            setIsEnableAutofillChecked(!isEnableAutofillChecked)
                        }
                        color={'secondary'}
                    />
                    <Typography>Enable Autofill</Typography>
                </div>
            </div> */}
        </div>
    )
}
